import { LT_FETCH } from 'bl/loggers.bl';
import SENTRY from 'config/sentry.singleton';
import UMSG from 'config/umsg.singleton';
import CT from 'constants/contentType.const';
import DELAY from 'constants/delay.const';
import HDR from 'constants/httpHeader.const';
import H from 'constants/httpMethod.const';
import { NO_MEDIA_TYPE } from 'constants/misc.const';
import UMSG_TYPE from 'constants/umsg/type.const';
import { BeAbortError, BeCallError, NoMainTokenError } from 'errors/be.error';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectLockToken } from 'selectors/simple';
import callMock from 'utils/backend/callMock.util';
import fetcher from 'utils/backend/fetcher.util';
import inspectError from 'utils/backend/inspectError.util';
import inspectResponse from 'utils/backend/inspectResponse.util';
import prepareBody from 'utils/backend/prepareBody.util';
import { selectMainToken } from 'utils/cognito.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToUmsgType from 'utils/converters/umsg/fromAnyToUmsgType.util';
import { nowAsZulu } from 'utils/date/now.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import parseJson from 'utils/parsers/parseJson';
import isFn from 'utils/predicates/function/isFn.util';
import replaceSalonId from 'utils/strings/replaceSalonId.util';

const L = logger(['beVersion4', LT_FETCH]);

const USE_CACHE_SAFE = true;
const USE_CACHE_IDEMPOTENT = true;
const USE_EVICT_IMMEDIATE = true;

const MTAG = 'beV4';
const LOGGER = 'callerV4';
const V = 4;

const createResponsePromise = async (optionsFromArgs, responseFromArgs) => {
  const { method, base, path, salonId, timeout } = optionsFromArgs ?? {};

  let text = void 1;
  let json = void 1;
  let is = void 1;
  let status = void 1;
  let ctype = void 1;

  try {
    const response = await responseFromArgs;

    is = inspectResponse(response);
    ctype = is.json ? CT.json : CT.text;

    // eslint-disable-next-line prefer-destructuring
    status = response?.status;
    if (is.noContent) return { response, ctype, is, status };

    text = await response?.text?.();
    json = is.json ? parseJson(text) : void 1;

    SENTRY.becall({ logger: LOGGER, v: V, salonId, status, method, base, path, text, json });

    return { response, ctype, is, status, text, json };
  } catch (e) {
    SENTRY.error({
      logger: LOGGER,
      v: V,
      tags: { salonId, method, base, path },
      extra: { status, text, json, ctype, is, timeout },
      error: e,
    });
    throw e;
  }
};

const isMessageNeeded = (umsgType, is) => false === is?.ok && (UMSG_TYPE.global === umsgType || UMSG_TYPE.fn === umsgType);

const repackageError = (options) => {
  const { ATAG, params, umsgType, umsg } = options ?? {};

  const cause = options?.error;
  const is = inspectError(cause);

  const tag = fromAnyToTrimmed(options?.tag);
  if (isMessageNeeded(umsgType, is)) UMSG.beFail({ ATAG, MTAG, is, error: cause, params, umsgType, umsg });

  const error = new BeCallError({ ...options, v: V, MTAG, is, cause });
  L.warn('repackageError()', MTAG, ATAG, tag, options, '->', cause, '->', error);

  return error;
};

const assertAuth = (options, state, headers) => {
  if (options?.auth) {
    const mt = selectMainToken(state);
    headers[HDR.mt] = `Bearer ${mt}`;

    if (!mt) throw new NoMainTokenError();
  }
};

const assertPin = (options, lt, path) => {
  if (options?.pin && !lt) {
    const error = new BeAbortError({ method: options?.method, path, why: 'pin-unsafe' });
    L.debug('()', error.message);
    throw error;
  }
};

const mockUp = (options) => {
  const { params, method, path, ATAG, fromDto, umsgType, base, headers, umsg, mock, timeout, body } = options ?? {};

  const callOptions = { v: 4, method, base, path, headers, body, timeout };
  L.debug('()', 'MOQ', ATAG, method, base + path, callOptions, '<-', options);

  const value = callMock(mock)(callOptions)
    .then((callResult) => {
      const receivedAt = nowAsZulu();
      const originalJson = callResult?.json;
      const json = isFn(fromDto) ? fromDto(originalJson) : originalJson;

      const result = {
        ...callResult,
        ATAG,
        MTAG,
        params,
        receivedAt,
        abort: void 1,
        body,
        json,
        originalJson,
        umsgType,
        umsg,
      };

      if (isMessageNeeded(umsgType, callResult?.is)) UMSG.bePass(result);

      return result;
    })
    .catch((error) =>
      repackageError({
        method,
        base,
        path,
        headers,
        body,
        timeout,
        error,
        ATAG,
        params,
        umsgType,
        umsg,
        tag: 'inner',
      }),
    );

  return { value, abort: void 1, isRegular: true };
};

const CACHE = new Map();

const beVersion4 = (options) => {
  const { base, params, timeout } = options ?? {};
  const { ctype, data, fromDto, toDto } = options ?? {};
  const { umsg, signal, store, mock } = options ?? {};
  const { state: _1, store: _2, ...optionsForKey } = options ?? {};

  let ATAG = void 1;
  let umsgType = void 1;
  let salonId = void 1;
  let method = void 1;
  let body = void 1;
  let state = void 1;
  let lt = void 1;
  let path = void 1;
  let headers = void 1;
  let url = void 1;
  let fetcherOptions = void 1;
  let fetcherResult = void 1;
  let abort = void 1;
  let receivedAt = void 1;

  let status = void 1;
  let text = void 1;
  let json = void 1;
  let is = void 1;
  let originalJson = void 1;

  try {
    ATAG = fromAnyToTrimmed(options?.ATAG);
    umsgType = options?.umsgType ?? fromAnyToUmsgType(umsg);

    state = isFn(store?.getState) ? store?.getState() : {};
    lt = selectLockToken(state);

    headers = { [HDR.ct]: 'application/json', ...(options?.headers ?? {}), [HDR.lt]: lt };
    // NOTE: options explicitly require no default content type set by NO_MEDIA_TYPE
    if (NO_MEDIA_TYPE === headers[HDR.ct]) delete headers[HDR.ct];

    // SEE: src/utils/backend/endpoint.util.js
    salonId = selectCurrentSalonId(state);
    path = replaceSalonId({ path: options?.path, salonId });

    assertAuth(options, state, headers);
    assertPin(options, lt, path);

    method = fromAnyToTrimmed(options?.method) || H.get;
    body = prepareBody({ data: isFn(toDto) ? toDto(data) : data, ctype, method }).body;

    if (isFn(mock)) {
      return mockUp({ mock, method, base, path, headers, body, timeout, fromDto, ATAG, params, umsgType, umsg });
    }

    url = replaceSalonId({ path: `${base}${path}`, salonId });
    fetcherOptions = { v: V, method, url, base, path, headers, body, timeout, state, store, signal };

    const key = JSON.stringify(optionsForKey);

    if (USE_CACHE_SAFE && (H.get === method || H.head === method || H.opt === method) && CACHE.has(key)) {
      const memoizedResult = CACHE.get(key);
      L.debug('()', CACHE.size, 'HIT', ATAG, method, base + path);
      return memoizedResult;
    }

    if (USE_CACHE_IDEMPOTENT && (H.put === method || H.del === method) && CACHE.has(key)) {
      const memoizedResult = CACHE.get(key);
      L.debug('()', CACHE.size, 'HIT', ATAG, method, base + path);
      return memoizedResult;
    }

    L.debug('()', 'REQ', ATAG, method, base + path, fetcherOptions, '<-', options);
    fetcherResult = fetcher(fetcherOptions);
    abort = fetcherResult?.abort;
    receivedAt = nowAsZulu();

    const value = createResponsePromise(fetcherOptions, fetcherResult?.response)
      .then(($) => {
        is = $?.is;
        status = $?.status;
        text = $?.text;
        originalJson = $?.json;
        json = isFn(fromDto) ? fromDto(originalJson) : originalJson;

        const result = { ...$, ATAG, MTAG, params, receivedAt, abort, body, json, originalJson, umsgType, umsg };
        if (isMessageNeeded(umsgType, $?.is)) UMSG.bePass(result);

        return result;
      })
      .catch((error) => repackageError({ ...fetcherOptions, error, ATAG, params, umsgType, umsg, tag: 'inner' }));

    if (USE_EVICT_IMMEDIATE) {
      value.finally(() => CACHE.delete(key));
    } else {
      value.finally(() => void delayed(DELAY.none, () => void CACHE.delete(key)));
    }

    const endResult = { value, abort, isRegular: true };
    CACHE.set(key, endResult);

    return endResult;
  } catch (error) {
    SENTRY.error({
      logger: LOGGER,
      v: V,
      tags: { salonId, method, base, path },
      extra: { status, text, json, ctype, is, timeout },
      error,
    });

    const value = Promise.resolve(repackageError({ ...fetcherOptions, error, ATAG, params, umsgType, umsg, tag: 'outer' }));
    return { value, abort, isRegular: false };
  }
};

beVersion4.MTAG = MTAG;

// noinspection JSUnusedGlobalSymbols
export default beVersion4;
